import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, FormControl, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { from, merge } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { PreferenceService } from '../../core';
import { CLEListItemModel, GetCLEListParams } from '../models/cle.models';
import { CLERepositoryService } from '../cle.repository';
import { ConfirmationService } from '../../shared';

@Component({
   selector: 'am-cle-list',
   templateUrl: 'cle-list.component.html',
   styleUrls: ['cle-list.component.scss']
})
export class CLEListComponent implements OnInit {
   @ViewChild(MatSort, { static: true }) sort: MatSort;
   @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

   dataSource = new MatTableDataSource<CLEListItemModel>([]);
   loading = true;
   columnsToDisplay = ['date', 'title', 'status', 'actions'];
   returnedItemsCount = 0;
   cases: CLEListItemModel[];
   filterForm: UntypedFormGroup;
   usersFilter = [];
   statusFilter = [];
   dateFilter = {};
   showFilters = false;

   constructor(
      private formBuilder: UntypedFormBuilder,
      private cleService: CLERepositoryService,
      private preferenceService: PreferenceService,
      private confirmation: ConfirmationService) {
   }

   ngOnInit() {
      this.initialize();
      this.subscribe();
      this.getCLEList();
   }

   toggleFilters() {
      this.showFilters = !this.showFilters;
   }

   getParameters() {
      const order = this.sort.active || 'startTime';
      this.dateFilter = this.filterForm.get('dateFilter').value;
      this.statusFilter = this.filterForm.get('statusFilter').value;
      this.usersFilter = this.filterForm.get('usersFilter').value;
      this.paginator.pageSize = this.paginator.pageSize || 25;
      const titleFilter = this.filterForm.get('titleFilter').value;

      this.savePreferences();

      const params: GetCLEListParams = {
         order: this.sort.direction === 'desc' ? '-' + order : order,
         page: this.paginator.pageIndex + 1,
         pageSize: this.paginator.pageSize,
         ...this.dateFilter,
         status: this.statusFilter,
         userIds: this.usersFilter,
         title: titleFilter
      };

      return params;
   }

   private getCLEList() {
      this.dataSource.data = [];

      this.loading = true;
      const params = this.getParameters();

      return this.cleService.getCLEList(params)
         .subscribe(data => {
            this.loading = false;
            this.returnedItemsCount = data.count;
            this.dataSource.data = data.list;
         });
   }

   export = () => {
      const params = this.getParameters();
      this.cleService.getCLEListDownload(params)
         .subscribe();
   }

   private subscribe() {
      this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
      merge(this.sort.sortChange, this.paginator.page, this.filterForm.valueChanges)
      .pipe(
         debounceTime(500)
      )
      .subscribe(() => this.getCLEList());
   }

   private initialize() {
      this.loadPreferences();

      this.filterForm = this.formBuilder.group({
         dateFilter: this.dateFilter,
         statusFilter: [this.statusFilter],
         usersFilter: [this.usersFilter],
         titleFilter: null
      });
   }

   private loadPreferences(): any {
      const preferences: any = this.preferenceService.getPreference('cleList');
      if (preferences) {
         Object.assign(this.sort, preferences.sortSettings);
         Object.assign(this.paginator, preferences.paginatorSettings);
         this.statusFilter = preferences.statusFilter;
         this.usersFilter = preferences.usersFilter;
         Object.assign(this.dateFilter, preferences.dateFilter);
      }
   }

   private savePreferences() {
      const preferences = {
         sortSettings: { active: this.sort.active, direction: this.sort.direction },
         paginatorSettings: { pageSize: this.paginator.pageSize },
         dateFilter: this.dateFilter,
         statusFilter: this.statusFilter,
         usersFilter: this.usersFilter
      };
      this.preferenceService.setPreference('cleList', preferences);
   }

   showDeleteDialog(cle: CLEListItemModel) {
      this.confirmation.confirm('Delete CLE?', 'DELETE', 'CANCEL').pipe(
         filter(ok => ok),
         switchMap(() => this.cleService.deleteCLE(cle.id))
      ).subscribe(() => {
         const index = this.dataSource.data.indexOf(cle);
         this.dataSource.data = [
            ...this.dataSource.data.slice(0, Math.max(index, 0)),
            ...this.dataSource.data.slice(index + 1)
         ];
      });
   }

   clearFilters() {
      this.filterForm.patchValue({
         dateFilter: {},
         legalTeamFilter: [],
         statusFilter: [],
         usersFilter: [],
         titleFilter: null
      });
   }
}



















